/**
 * Spatial Analysis drawer — presentational view over SpatialAnalysisContext.
 *
 * The map stays the hero surface; this dense right drawer shows run status, a
 * progress timeline (with a throbbing orange icon on the active step), live
 * metrics, generated layers, evidence notes, and a progressively-drafted report
 * at the bottom that expands into a modal. All run state and polling live in the
 * context, so closing this drawer keeps the run going in the background.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Drawer from '@mui/material/Drawer';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

import AccessibleIcon from '@mui/icons-material/Accessible';
import CakeIcon from '@mui/icons-material/Cake';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import GroupsIcon from '@mui/icons-material/Groups';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import PaymentsIcon from '@mui/icons-material/Payments';
import HubIcon from '@mui/icons-material/Hub';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LayersIcon from '@mui/icons-material/Layers';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import PlaceIcon from '@mui/icons-material/Place';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { Polygon } from '../../../context/context';
import { MAP_UI_TOP_BAR_HEIGHT_PX } from '../mapUiLayout';
import { trackAnalyticsEvent } from '../../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../../services/eventTaxonomy';
import { useSpatialAnalysis } from '../../../context/SpatialAnalysisContext';
import {
  ReportsUnavailableError,
  SavedReport,
  saveSpatialAnalysisReport,
} from '../../../services/spatialAnalysis/spatialAnalysisReportService';
import { buildSpatialAnalysisReportTitle } from '../../../services/spatialAnalysis/reportTitle';
import SpatialAnalysisSavedReports from './SpatialAnalysisSavedReports';
import {
  SpatialAnalysisLayer,
  SpatialAnalysisMetrics,
  SpatialAnalysisReportSection,
  SpatialAnalysisRunStatus,
  SpatialAnalysisStep,
} from '../../../services/spatialAnalysis/types';
import { LIFTANGO_ORANGE, STEP_ICON, throb } from './spatialAnalysisIcons';

interface SpatialAnalysisDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Polygons available to analyse (for the in-drawer selector). */
  polygons?: Array<Polygon & { _layerName?: string }>;
  /** Start a run for the chosen polygon. */
  onRunSpatialAnalysis?: (polygon: Polygon) => void;
  /** Optional: focus a generated layer on the map by its spatial layer id. */
  onFocusLayer?: (spatialLayerId: string) => void;
  /**
   * True while the post-complete GTFS feed import (owned by map.tsx) is in flight.
   * Shows a compact "Loading transit routes…" line near the transit metrics.
   */
  gtfsImporting?: boolean;
}

const STATUS_CHIP: Record<
  SpatialAnalysisRunStatus,
  { color: 'default' | 'info' | 'primary' | 'warning' | 'success' | 'error'; tone: 'neutral' | 'info' | 'active' | 'warning' | 'success' | 'error' }
> = {
  queued: { color: 'info', tone: 'info' },
  running: { color: 'primary', tone: 'active' },
  drafting: { color: 'warning', tone: 'warning' },
  complete: { color: 'success', tone: 'success' },
  failed: { color: 'error', tone: 'error' },
  cancelled: { color: 'default', tone: 'neutral' },
};

const STATUS_TONE = {
  neutral: { color: '#61666e', bg: '#eceef2', dot: '#787f8a' },
  info: { color: '#3f5e92', bg: '#e9eef7', dot: '#7c96bc' },
  active: { color: '#ea6626', bg: '#fdece1', dot: '#ea6626' },
  warning: { color: '#b77410', bg: '#fbefd8', dot: '#b77410' },
  success: { color: '#3f7d4d', bg: '#e3f1e5', dot: '#3f7d4d' },
  error: { color: '#c5392b', bg: '#fbe7e3', dot: '#c5392b' },
} as const;

function StepIcon({ step, isActive }: { step: SpatialAnalysisStep; isActive: boolean }): React.ReactElement {
  if (step.status === 'completed') {
    return <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />;
  }
  if (step.status === 'failed') {
    return <WarningAmberIcon fontSize="small" sx={{ color: 'error.main' }} />;
  }
  if (isActive || step.status === 'running') {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          color: LIFTANGO_ORANGE,
          animation: `${throb} 1.2s ease-in-out infinite`,
        }}
      >
        {STEP_ICON[step.id] ?? <RadioButtonUncheckedIcon fontSize="small" />}
      </Box>
    );
  }
  return <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'text.disabled' }} />;
}

function MetricRow({ label, value }: { label: string; value: React.ReactNode }): React.ReactElement | null {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  return (
    <Box
      sx={(theme) => ({
        minHeight: 54,
        p: 1,
        border: `1px solid ${theme.palette.surface.borderSubtle}`,
        borderRadius: 1,
        backgroundColor: alpha(theme.palette.surface.secondary, 0.56),
      })}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ mt: 0.35, fontWeight: 700, fontFamily: 'monospace', color: 'text.primary' }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function fmt(n?: number): string | undefined {
  if (n === undefined || n === null) {
    return undefined;
  }
  return n.toLocaleString();
}

/** Format a 0..1 share as a whole-percent string (e.g. 0.183 -> "18%"). */
function pct(share?: number): string | undefined {
  if (share === undefined || share === null || Number.isNaN(share)) {
    return undefined;
  }
  return `${Math.round(share * 100)}%`;
}

const TRANSPORT_MODE_ICON: Record<string, React.ReactElement> = {
  car: <DirectionsCarIcon fontSize="inherit" />,
  transit: <DirectionsBusIcon fontSize="inherit" />,
  walk: <DirectionsWalkIcon fontSize="inherit" />,
  bike: <DirectionsBikeIcon fontSize="inherit" />,
  home: <HomeWorkIcon fontSize="inherit" />,
};

/** A compact label/value row that leads with a semantic icon. */
function CensusStat({
  icon,
  label,
  value,
}: {
  icon: React.ReactElement;
  label: string;
  value: React.ReactNode;
}): React.ReactElement | null {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
      <Box sx={{ display: 'inline-flex', color: 'text.secondary', fontSize: 18, flex: '0 0 auto' }}>
        {icon}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ flex: '0 0 auto' }}>
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, fontFamily: 'monospace', color: 'text.primary', ml: 'auto', whiteSpace: 'nowrap' }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/**
 * Richer census dimensions (US ACS today): median age, age-band split,
 * top transport modes, disability prevalence, median household income. Each
 * row is shown only when the underlying field is present (null/undefined hidden).
 */
function CensusDimensions({
  population,
  t,
}: {
  population: NonNullable<SpatialAnalysisMetrics['population']>;
  t: (k: string) => string;
}): React.ReactElement | null {
  const { medianAgeYears, ageGroupDistribution, transportModeShare, disabilityPrevalence, medianHouseholdIncome, incomeCurrency, incomePeriod, source } =
    population;

  // A real median age / household income is never 0, so treat 0 (alongside
  // null/undefined) as "no data" and hide the row — e.g. AU returns 0/null
  // for these ACS-only fields and we must not render "Median age 0".
  const hasMedianAge = typeof medianAgeYears === 'number' && medianAgeYears > 0;
  const hasMedianIncome = typeof medianHouseholdIncome === 'number' && medianHouseholdIncome > 0;

  const ageSplit = ageGroupDistribution
    ? ['0-14', '15-64', '65+']
        .filter((band) => ageGroupDistribution[band] !== undefined)
        .map((band) => `${band} ${pct(ageGroupDistribution[band])}`)
        .join(' · ')
    : undefined;

  const topModes = transportModeShare
    ? Object.entries(transportModeShare)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3)
    : [];

  // Compact period suffix so the income figure reads unambiguously
  // (weekly → /wk, monthly → /mo, annual → /yr); omitted when period unknown.
  const incomePeriodSuffix =
    incomePeriod === 'weekly' ? '/wk' : incomePeriod === 'monthly' ? '/mo' : incomePeriod === 'annual' ? '/yr' : '';
  const income = hasMedianIncome
    ? `${incomeCurrency ? `${incomeCurrency} ` : ''}${Math.round(medianHouseholdIncome).toLocaleString()}${incomePeriodSuffix}`
    : undefined;

  const hasAnything =
    hasMedianAge ||
    ageSplit !== undefined ||
    topModes.length > 0 ||
    disabilityPrevalence !== undefined ||
    income !== undefined;
  if (!hasAnything) {
    return null;
  }

  return (
    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
      <CensusStat
        icon={<CakeIcon fontSize="inherit" />}
        label={t('spatialAnalysis.metrics.medianAge')}
        value={hasMedianAge ? `${Math.round(medianAgeYears * 10) / 10}` : undefined}
      />
      <CensusStat icon={<GroupsIcon fontSize="inherit" />} label={t('spatialAnalysis.metrics.ageGroups')} value={ageSplit} />
      {topModes.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'inline-flex', color: 'text.secondary', fontSize: 18 }}>
            <DirectionsBusIcon fontSize="inherit" />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t('spatialAnalysis.metrics.transportModes')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto', flexWrap: 'wrap' }}>
            {topModes.map(([mode, share]) => (
              <Box key={mode} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                <Box sx={{ display: 'inline-flex', color: 'text.secondary', fontSize: 16 }}>
                  {TRANSPORT_MODE_ICON[mode] ?? <DirectionsBusIcon fontSize="inherit" />}
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                  {t(`spatialAnalysis.metrics.mode.${mode}`)} {pct(share)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
      <CensusStat
        icon={<AccessibleIcon fontSize="inherit" />}
        label={t('spatialAnalysis.metrics.disability')}
        value={pct(disabilityPrevalence)}
      />
      <CensusStat icon={<PaymentsIcon fontSize="inherit" />} label={t('spatialAnalysis.metrics.medianIncome')} value={income} />
      {source && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled', flex: '0 0 auto' }} />
          <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
            {source}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function MetricsPanel({ metrics, t }: { metrics: SpatialAnalysisMetrics; t: (k: string) => string }): React.ReactElement {
  const { studyArea, population, transit, gaps, transitDeserts, drt } = metrics;
  return (
    <>
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
      {studyArea && (
        <>
          <MetricRow label={t('spatialAnalysis.metrics.area')} value={studyArea.areaKm2 ? `${studyArea.areaKm2} km2` : undefined} />
          <MetricRow label={t('spatialAnalysis.metrics.vertices')} value={fmt(studyArea.vertexCount)} />
        </>
      )}
      {population && (
        <MetricRow label={t('spatialAnalysis.metrics.population')} value={fmt(population.worldPopEstimate)} />
      )}
      {transit && (
        <>
          <MetricRow label={t('spatialAnalysis.metrics.stops')} value={fmt(transit.stopCount)} />
          <MetricRow label={t('spatialAnalysis.metrics.routes')} value={fmt(transit.routeCount)} />
        </>
      )}
      {gaps && (
        <MetricRow
          label={t('spatialAnalysis.metrics.uncoveredPopulation')}
          value={fmt(gaps.uncoveredPopulationEstimate)}
        />
      )}
      {transitDeserts && transitDeserts.primaryUncoveredPopulation != null && (
        <MetricRow
          label={t('spatialAnalysis.metrics.transitDesertPrimary')}
          value={fmt(Math.round(transitDeserts.primaryUncoveredPopulation))}
        />
      )}
      {drt && (
        <>
          <MetricRow label={t('spatialAnalysis.metrics.drtCandidates')} value={fmt(drt.candidateCount)} />
          <MetricRow label={t('spatialAnalysis.metrics.dailyTrips')} value={fmt(drt.estimatedDailyTrips)} />
          <MetricRow label={t('spatialAnalysis.metrics.vehicles')} value={fmt(drt.estimatedVehiclesNeeded)} />
        </>
      )}
    </Box>
    {transit?.source === 'bods' && (
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Chip
          icon={<DirectionsBusIcon fontSize="small" />}
          size="small"
          variant="outlined"
          color="primary"
          label={t('spatialAnalysis.metrics.bodsSource')}
          sx={{ alignSelf: 'flex-start' }}
        />
        {transit.attribution && (
          <Typography variant="caption" color="text.secondary">
            {transit.attribution}
          </Typography>
        )}
      </Box>
    )}
    {population && <CensusDimensions population={population} t={t} />}
    </>
  );
}

function LayerRow({
  layer,
  onFocusLayer,
}: {
  layer: SpatialAnalysisLayer;
  onFocusLayer?: (id: string) => void;
}): React.ReactElement {
  return (
    <Box
      onClick={onFocusLayer ? () => onFocusLayer(layer.id) : undefined}
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        flex: '0 0 auto',
        minHeight: 30,
        maxWidth: '100%',
        px: 1,
        py: 0.5,
        borderRadius: 999,
        border: `1px solid ${theme.palette.surface.borderSubtle}`,
        backgroundColor: alpha(theme.palette.surface.secondary, 0.56),
        whiteSpace: 'nowrap',
        cursor: onFocusLayer ? 'pointer' : 'default',
        '&:hover': onFocusLayer
          ? { backgroundColor: alpha(theme.palette.actionBrand.primary, 0.07) }
          : undefined,
      })}
    >
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: '3px',
          backgroundColor: layer.style.fillColor ?? layer.style.color,
          border: `2px solid ${layer.style.color}`,
          flexShrink: 0,
        }}
      />
      <Typography variant="body2" noWrap>{layer.name.replace('Spatial Analysis - ', '')}</Typography>
    </Box>
  );
}

function SectionHeading({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }): React.ReactElement {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 1 }}>
      <Box sx={{ display: 'inline-flex', color: 'text.secondary' }}>{icon}</Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {children}
      </Typography>
    </Box>
  );
}

function StatusBadge({
  label,
  tone,
  pulse,
}: {
  label: string;
  tone: keyof typeof STATUS_TONE;
  pulse?: boolean;
}): React.ReactElement {
  const config = STATUS_TONE[tone];
  return (
    <Box
      role="status"
      aria-label={label}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        py: 0.45,
        borderRadius: 999,
        fontSize: '0.68rem',
        fontWeight: 800,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
        color: config.color,
        backgroundColor: config.bg,
        border: pulse ? `1px solid ${config.color}` : '1px solid transparent',
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: config.dot,
          boxShadow: pulse ? `0 0 6px ${config.dot}` : 'none',
          animation: pulse ? `${throb} 1.4s ease-in-out infinite` : 'none',
          flexShrink: 0,
        }}
      />
      {label}
    </Box>
  );
}

function ProgressGrid({
  steps,
  activeStepId,
}: {
  steps: SpatialAnalysisStep[];
  activeStepId?: string | null;
}): React.ReactElement {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
      {steps.map((step) => {
        const isActive = step.id === activeStepId;
        return (
          <Box
            key={step.id}
            sx={(theme) => ({
              display: 'grid',
              gridTemplateColumns: '24px minmax(0, 1fr)',
              alignItems: 'center',
              gap: 0.75,
              minHeight: 46,
              p: 1,
              borderRadius: 1,
              border: `1px solid ${isActive ? alpha(theme.palette.actionBrand.primary, 0.52) : theme.palette.surface.borderSubtle}`,
              backgroundColor: isActive
                ? alpha(theme.palette.actionBrand.primary, 0.08)
                : alpha(theme.palette.surface.secondary, 0.5),
            })}
          >
            <StepIcon step={step} isActive={isActive} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: isActive ? 700 : 600, lineHeight: 1.25 }}>
                {step.label}
              </Typography>
              {step.summary && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {step.summary}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default function SpatialAnalysisDrawer({
  open,
  onClose,
  polygons = [],
  onRunSpatialAnalysis,
  onFocusLayer,
  gtfsImporting = false,
}: SpatialAnalysisDrawerProps): React.ReactElement {
  const { t } = useTranslation('map');
  const { run, status, activeStepId, isStarting, error, cancelRun } = useSpatialAnalysis();
  const [reportOpen, setReportOpen] = useState(false);
  // When a saved report is opened it overrides the run's report in the expanded
  // modal. null = show the current run's report.
  const [viewedReport, setViewedReport] = useState<{ title: string; sections: SpatialAnalysisReportSection[] } | null>(null);
  const [selectedPolygonId, setSelectedPolygonId] = useState('');

  const polygonShapes = polygons.filter((p): p is Polygon & { _layerName?: string } => p.type === 'polygon');

  const handleRunSelected = () => {
    const polygon = polygonShapes.find((p) => p.id === selectedPolygonId);
    if (polygon && onRunSpatialAnalysis) {
      onRunSpatialAnalysis(polygon);
    }
  };

  const isRunning = status === 'queued' || status === 'running' || status === 'drafting';
  const statusLabel = useMemo(() => {
    if (status === 'idle') {
      return t('spatialAnalysis.status.idle');
    }
    return t(`spatialAnalysis.status.${status}`);
  }, [status, t]);
  const statusTone: keyof typeof STATUS_TONE = status === 'idle' ? 'neutral' : STATUS_CHIP[status].tone;

  const reportSections = run?.report?.sections ?? [];

  // Close the expanded report if the run is cleared/replaced and has no report —
  // but not while a saved report is being viewed (it carries its own content).
  React.useEffect(() => {
    if (reportSections.length === 0 && !viewedReport) {
      setReportOpen(false);
    }
  }, [reportSections.length, viewedReport]);

  const handleExpandReport = () => {
    if (run) {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.spatial_analysis_report_expanded, {
        run_id: run.id,
        section_count: reportSections.length,
        source: 'run',
        timestamp: new Date().toISOString(),
      });
    }
    setViewedReport(null);
    setReportOpen(true);
  };

  const handleOpenSavedReport = (report: SavedReport) => {
    setViewedReport({ title: report.title, sections: report.report_sections ?? [] });
    setReportOpen(true);
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.spatial_analysis_report_expanded, {
      report_id: report.report_id,
      section_count: (report.report_sections ?? []).length,
      source: 'saved',
      timestamp: new Date().toISOString(),
    });
  };

  const [savedRefreshToken, setSavedRefreshToken] = useState(0);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'unavailable' | 'error'>('idle');

  const handleSaveReport = async () => {
    if (!run) {
      return;
    }
    setSaveStatus('saving');
    try {
      const title = await buildSpatialAnalysisReportTitle(run.source, run.metrics, t('spatialAnalysis.title'));
      await saveSpatialAnalysisReport({
        title,
        source_summary: run.source,
        metrics: run.metrics,
        report_sections: reportSections,
        layers: run.layers,
      });
      setSaveStatus('saved');
      setSavedRefreshToken((token) => token + 1);
    } catch (error) {
      setSaveStatus(error instanceof ReportsUnavailableError ? 'unavailable' : 'error');
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="persistent"
      sx={{ zIndex: 2201 }}
      PaperProps={{
        sx: (theme) => ({
          // Offset 65px from the right edge (and below the top bar) so the
          // right-hand drawer rail stays visible and this drawer doesn't occlude
          // the other map drawers — matching the sibling drawers (Population
          // Analysis / Fixed Routes). Without the right offset it sits flush at
          // right:0 and hides the rail.
          width: 'calc(100% - 65px)',
          maxWidth: 560,
          right: 65,
          top: MAP_UI_TOP_BAR_HEIGHT_PX,
          height: `calc(100vh - ${MAP_UI_TOP_BAR_HEIGHT_PX}px)`,
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          // Solid (no backdrop blur) so the drawer is clearly readable above the map.
          backgroundColor: theme.palette.background.paper,
          borderLeft: `1px solid ${theme.palette.surface.borderSubtle}`,
          boxShadow: theme.shadows[8],
          zIndex: 2201,
        }),
      }}
    >
      {/* Header */}
      <Box
        sx={(theme) => ({
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.surface.borderSubtle}`,
          backgroundColor: alpha(theme.palette.surface.secondary, 0.42),
        })}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LayersIcon sx={{ color: LIFTANGO_ORANGE }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.15 }}>
              {t('spatialAnalysis.title')}
            </Typography>
            {run?.source.polygonName && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {run.source.polygonName}
              </Typography>
            )}
          </Box>
          <StatusBadge label={statusLabel} tone={statusTone} pulse={isRunning} />
          {isStarting && <CircularProgress size={16} sx={{ color: LIFTANGO_ORANGE }} />}
          {isRunning && (
            <Tooltip title={t('spatialAnalysis.actions.stop')}>
              <IconButton size="small" color="error" onClick={cancelRun} aria-label={t('spatialAnalysis.actions.stop')}>
                <StopCircleIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={onClose} aria-label={t('spatialAnalysis.actions.close')}>
            <CloseIcon />
          </IconButton>
        </Box>
        {run?.progress && run.progress.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1.25 }} aria-hidden="true">
            {run.progress.map((step) => (
              <Box
                key={step.id}
                sx={(theme) => ({
                  height: 4,
                  flex: 1,
                  borderRadius: 999,
                  backgroundColor:
                    step.status === 'completed'
                      ? theme.palette.success.main
                      : step.id === activeStepId || step.status === 'running'
                        ? theme.palette.actionBrand.primary
                        : alpha(theme.palette.text.primary, 0.12),
                })}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Scrollable body */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 2 }}>
        {/* Polygon selector + run (entry point when opened from the rail / between runs) */}
        {!isRunning && onRunSpatialAnalysis && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              gap: 1,
              alignItems: 'center',
              mt: 2,
              mb: 1,
            }}
          >
            <FormControl fullWidth size="small">
              <InputLabel id="sa-polygon-label">{t('spatialAnalysis.selectPolygon')}</InputLabel>
              <Select
                labelId="sa-polygon-label"
                label={t('spatialAnalysis.selectPolygon')}
                value={selectedPolygonId}
                onChange={(e) => setSelectedPolygonId(e.target.value as string)}
                disabled={polygonShapes.length === 0}
                MenuProps={{ sx: { zIndex: 2305 }, PaperProps: { sx: { zIndex: 2306 } } }}
              >
                {polygonShapes.map((polygon) => (
                  <MenuItem key={polygon.id} value={polygon.id}>
                    {polygon._layerName ? `${polygon._layerName}: ` : ''}
                    {polygon.name || polygon.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              sx={{ minHeight: 40, px: 2, whiteSpace: 'nowrap' }}
              disabled={!selectedPolygonId}
              onClick={handleRunSelected}
            >
              {t('spatialAnalysis.actions.run')}
            </Button>
            {polygonShapes.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
                {t('spatialAnalysis.empty')}
              </Typography>
            )}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {status === 'cancelled' && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('spatialAnalysis.cancelledNotice')}
          </Alert>
        )}

        {status === 'failed' && run?.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {run.error}
          </Alert>
        )}

        {run && (
          <>
            {/* Progress timeline */}
            <SectionHeading icon={<HubIcon fontSize="small" />}>
              {t('spatialAnalysis.sections.progress')}
            </SectionHeading>
            <ProgressGrid steps={run.progress} activeStepId={activeStepId} />

            {/* Live metrics */}
            <SectionHeading icon={<GroupsIcon fontSize="small" />}>
              {t('spatialAnalysis.sections.metrics')}
            </SectionHeading>
            <MetricsPanel metrics={run.metrics} t={t} />

            {/* Transit routes import in flight: after the run reports `complete`,
                the clipped GTFS feed is still being fetched + converted into the
                Fixed Routes drawer (owned by map.tsx). Surface that so the user
                knows transit routes are still arriving on the map. */}
            {gtfsImporting && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
                <CircularProgress size={14} sx={{ color: LIFTANGO_ORANGE, flex: '0 0 auto' }} />
                <Typography variant="caption" color="text.secondary">
                  {t('spatialAnalysis.loadingTransitRoutes')}
                </Typography>
              </Box>
            )}

            {/* Why transit deserts don't trace the full network: deserts are gaps
                beyond a walk buffer of FREQUENT (≤30 min) stops, while the network
                drawn above includes less-frequent routes. Footnote, shown only when
                we actually have desert data so it never appears spuriously. */}
            {run.metrics.transitDeserts &&
              run.metrics.transitDeserts.primaryUncoveredPopulation != null && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 1 }}>
                  <InfoOutlinedIcon
                    fontSize="inherit"
                    sx={{ color: 'text.secondary', fontSize: 14, mt: '2px', flex: '0 0 auto' }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                    {t('spatialAnalysis.transitDesertNote')}
                  </Typography>
                </Box>
              )}

            {/* GTFS availability: when the run is complete but no clipped GTFS feed
                was loaded, say so explicitly (the map transit is OSM-only and the
                Fixed Routes drawer stays empty by design, not by failure). */}
            {status === 'complete' && run.metrics.transit?.gtfsFeedAvailable === false && (
              <Alert severity="info" icon={<DirectionsBusIcon fontSize="inherit" />} sx={{ mt: 1.5, py: 0.25 }}>
                {t('spatialAnalysis.noGtfsNotice')}
              </Alert>
            )}

            {/* Layer controls */}
            {run.layers.length > 0 && (
              <>
                <SectionHeading icon={<LayersIcon fontSize="small" />}>
                  {t('spatialAnalysis.sections.layers')}
                </SectionHeading>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {run.layers.map((layer) => (
                    <LayerRow key={layer.id} layer={layer} onFocusLayer={onFocusLayer} />
                  ))}
                </Box>
              </>
            )}

            {/* Evidence notes */}
            {run.notes.length > 0 && (
              <>
                <SectionHeading icon={<PlaceIcon fontSize="small" />}>
                  {t('spatialAnalysis.sections.evidence')}
                </SectionHeading>
                {run.notes.map((note) => (
                  <Box
                    key={note.id}
                    onClick={note.layerId && onFocusLayer ? () => onFocusLayer(note.layerId as string) : undefined}
                    sx={(theme) => ({
                      display: 'grid',
                      gridTemplateColumns: '8px minmax(0, 1fr)',
                      gap: 1,
                      alignItems: 'start',
                      py: 0.75,
                      cursor: note.layerId && onFocusLayer ? 'pointer' : 'default',
                      borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.07)}`,
                      '&:last-of-type': { borderBottom: 0 },
                    })}
                  >
                    <Box
                      sx={(theme) => ({
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        mt: 0.75,
                        backgroundColor: theme.palette.actionBrand.primary,
                      })}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                      {note.text}
                    </Typography>
                  </Box>
                ))}
              </>
            )}

            {/* Report draft at the bottom */}
            {reportSections.length > 0 && (
              <Box
                sx={(theme) => ({
                  mt: 2,
                  p: 1.25,
                  textAlign: 'left',
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.surface.borderSubtle}`,
                  backgroundColor: alpha(theme.palette.surface.secondary, 0.44),
                })}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <DescriptionIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {t('spatialAnalysis.sections.report')}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveReport}
                    disabled={saveStatus === 'saving'}
                  >
                    {saveStatus === 'saving'
                      ? t('spatialAnalysis.savedReports.saving')
                      : t('spatialAnalysis.savedReports.save')}
                  </Button>
                  <Button size="small" startIcon={<OpenInFullIcon />} onClick={handleExpandReport}>
                    {t('spatialAnalysis.actions.expandReport')}
                  </Button>
                </Box>
                {saveStatus === 'saved' && (
                  <Alert severity="success" sx={{ mb: 1, py: 0 }}>
                    {t('spatialAnalysis.savedReports.saved')}
                  </Alert>
                )}
                {saveStatus === 'unavailable' && (
                  <Alert severity="info" sx={{ mb: 1, py: 0 }}>
                    {t('spatialAnalysis.savedReports.underConstructionTitle')}
                  </Alert>
                )}
                {saveStatus === 'error' && (
                  <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
                    {t('spatialAnalysis.savedReports.saveError')}
                  </Alert>
                )}
                {reportSections.slice(0, 3).map((section) => (
                  <Box
                    key={section.id}
                    sx={(theme) => ({
                      py: 0.9,
                      borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                    })}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'left' }}>
                      {section.heading}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, textAlign: 'left' }}>
                      {section.body}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}

        {/* My Saved Reports (user-scoped; always available to browse) */}
        <SectionHeading icon={<BookmarksIcon fontSize="small" />}>
          {t('spatialAnalysis.savedReports.title')}
        </SectionHeading>
        <SpatialAnalysisSavedReports refreshToken={savedRefreshToken} onOpenReport={handleOpenSavedReport} />
      </Box>

      {/* Expanded report modal */}
      <Dialog
        open={reportOpen}
        onClose={() => {
          setReportOpen(false);
          setViewedReport(null);
        }}
        maxWidth="md"
        fullWidth
        // The drawer Paper sits at a custom zIndex (2201), above MUI's default
        // modal layer (1300), so a default Dialog renders BEHIND it. Raise this
        // modal above the drawer (and the 2300 top bar / 2305 menus), matching
        // the AttachOperationsModal convention. Theme-relative so it tracks MUI.
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1300 }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon sx={{ color: LIFTANGO_ORANGE }} />
          {viewedReport ? viewedReport.title : t('spatialAnalysis.reportTitle')}
          <Box sx={{ flex: 1 }} />
          <IconButton
            size="small"
            onClick={() => {
              setReportOpen(false);
              setViewedReport(null);
            }}
            aria-label={t('spatialAnalysis.actions.close')}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ textAlign: 'left' }}>
          {(viewedReport ? viewedReport.sections : reportSections).map((section) => (
            <Box key={section.id} sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'left' }}>
                {section.heading}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                {section.body}
              </Typography>
            </Box>
          ))}
        </DialogContent>
      </Dialog>
    </Drawer>
  );
}
